import {
  applyFnToProperty,
  belowThresholdFillWithZero,
  clone,
  Inverter,
  InverterV2,
  isProductEnerclic,
  isProductHybrid,
  isProductMeter,
  isProductStorage,
  MathAvg,
  objectPropertiesToString,
  PowerFlowGraphMode,
  ProductModelService,
  SystemPowerState,
} from "ngx-core-services";
import { Observable, of } from "rxjs";

export type NodePowerSource = {
  label: 'Battery' | 'Solar' | 'Grid';
  value: number;
};

export type NodePowerDestination = {
  label: 'Home' | 'Battery' | 'Grid';
  weight: number;
};

export const BuildPowerFlow = (cT: Partial<InverterV2>): SystemPowerState => {
  /**
   * If there is only power generated by the inverter, then the power go to the home
   */
  if (!Number(cT.P_EXP) && !Number(cT.P_LOAD) && !Number(cT.P_B_AGGR)  && Number(cT.P_DC_TOT)) {
    cT.P_LOAD = cT.P_DC_TOT;
  }

  const home = Number(cT.P_LOAD);
  const solar = Number(cT.P_DC_TOT);
  const batteryPower = Math.abs(Number(cT.P_B_AGGR || 0));
  const batteryD = Number(cT.P_B_AGGR || 0) > 0 ? Number(cT.P_B_AGGR) : 0;
  const batteryS = Number(cT.P_B_AGGR || 0) < 0 ? Number(cT.P_B_AGGR) * - 1 : 0;
  const gridD = Number(cT.P_EXP || 0) > 0 ? Number(cT.P_EXP) : 0;
  const gridS = Number(cT.P_EXP || 0) < 0 ? Number(cT.P_EXP) * -1 : 0;

  // Solarimeter: preserva undefined se il valore non esiste nel backend
  const irradiance = cT.SOL_IRR != null ? Number(cT.SOL_IRR) : undefined;
  const panelTemperature = cT.SOL_TEMP != null ? Number(cT.SOL_TEMP) : undefined;
  const solarimeter = (irradiance !== undefined || panelTemperature !== undefined)
    ? { irradiance, panelTemperature }
    : undefined;

  const links = getFlowDistribution(home, batteryS, batteryD, solar, gridS, gridD);

  /**
   * Overwrite the powers that are below the 50W threshold with 0
   */
  const MIN_POWER_THRESHOLD_W = 50;
  const keyException = ['battery'];
  const normalized = belowThresholdFillWithZero({
    solar,
    home,
    batteryPower,
    grid:  Math.abs(Number(cT.P_EXP || 0)),
    battery: Number(cT.BP),
    fromSolarToHome: links.fromSolarToHome || 0,
    fromSolarToBattery: links.fromSolarToBattery || 0,
    fromSolarToGrid: links.fromSolarToGrid || 0,
    fromGridToHome: links.fromGridToHome || 0,
    fromBatteryToHome: links.fromBatteryToHome || 0,
    fromBatteryToGrid: links.fromBatteryToGrid || 0,
    fromGridToBattery: links.fromGridToBattery || 0,
  }, MIN_POWER_THRESHOLD_W,  keyException);
  return { ...normalized, ts: Number(cT.TS), solarimeter };
};

export const getFlowDistribution = (
    home: number,
    batteryS: number,
    batteryD: number,
    solar: number,
    gridS: number,
    gridD: number,
  ): {
    fromSolarToHome?: number;
    fromSolarToBattery?: number;
    fromSolarToGrid?: number;
    fromGridToHome?: number;
    fromBatteryToHome?: number;
    fromBatteryToGrid?: number;
    fromGridToBattery?: number;
  } => {
  const _source: NodePowerSource[] = [
    {
      label: 'Grid',
      value: gridS,
    },
    {
      label: 'Battery',
      value: batteryS,
    },
    {
      label: 'Solar',
      value: solar,
    },
  ];
  const _destination: NodePowerDestination[] = [
    {
      label: 'Home',
      weight: home,
    },
    {
      label: 'Battery',
      weight: batteryD,
    },
    {
      label: 'Grid',
      weight: gridD,
    },
  ];

  let links = {};
  // eslint-disable-next-line @typescript-eslint/prefer-for-of
  for (let indexD = 0; indexD < _destination.length; indexD++) {
    let weight = _destination[indexD].weight;
    const labelD = _destination[indexD].label;
    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let indexS = 0; indexS < _source.length; indexS++) {
      const { label: labelS, value } = _source[indexS];
      if (value === 0) {
        continue;
      }
      const temp = value - weight;
      if (temp === 0) {
        links = {
          ...links,
          [`from${labelS}To${labelD}`]: value,
        };
        _source[indexS].value = 0;
        break;
      } else if (temp > 0) {
        links = {
          ...links,
          [`from${labelS}To${labelD}`]: weight,
        };
        _source[indexS].value = temp;
        break;
      } else {
        links = {
          ...links,
          [`from${labelS}To${labelD}`]: value,
        };
        _source[indexS].value = 0;
        weight = -temp;
      }
    }
  }
  console.groupEnd();
  return links;
};

export const getSnapshotPowerAggregate = (telemetry: {
  [key: string]: Partial<Inverter>;
}, plantType: PowerFlowGraphMode, invertMeterSensor: boolean): Partial<Inverter> => {
  if (plantType === PowerFlowGraphMode.WithBattery) {
    return getHybridSystemPowerAggregate(telemetry);
  }
  if (plantType === PowerFlowGraphMode.WithImportExport) {
    return getImportExportSystemPowerAggregate(telemetry, invertMeterSensor);
  }
  if (plantType === PowerFlowGraphMode.OnlyGeneratedPower) {
    return getPhotovoltaicSystemPowerAggregate(telemetry);
  }

  throw new Error('plant type not found');
};

export const getHybridSystemPowerAggregate = (telemetry: {
  [key: string]: Partial<Inverter>;
}): Partial<Inverter> => {
  const devicesWhitBattery = Object.values(telemetry).filter(
    (v) =>
      (v.BP !== undefined && v.BP !== null && Number(v.BP) !== 0 && !Number.isNaN(Number(v.BP))) ||
      (v.BP1 !== undefined && v.BP1 !== null && Number(v.BP1) !== 0 && !Number.isNaN(Number(v.BP1))) ||
      (v.BP2 !== undefined && v.BP2 !== null && Number(v.BP2) !== 0 && !Number.isNaN(Number(v.BP2)))
  );
  // @note: we want insert on the first positions all inverters
  // necessary to have a correct power when there are meters on the system
  const devices = Object.keys(telemetry).map((id) => (new ProductModelService(id)));
  const sortedKeyDevices = devices.filter((pm) => pm.type?.name === 'inverter').concat(
    devices.filter((pm) => pm.type?.name !== 'inverter')
  ).map((pm) => pm.id);

  return objectPropertiesToString(
    sortedKeyDevices.reduce(
      (prev, deviceId) => {
        const cT: Partial<Inverter & InverterV2> = clone(telemetry[deviceId]);
        const product = new ProductModelService(deviceId);
        // @note: for hybrid-3ph power-generated is Production = Mp1 + Mp2 + p9 (external production)
        // @note: for hybrid-3ph power charging(discharging) & power importing(exporting) using 1 variable (positive or negative)
        // @note: for hybrid-3ph has two batteries therefore two charging / discharge values
        if ((isProductHybrid(product)) || isProductStorage(product)) {
          if (parseInt(`${cT.V}`) !== 3) {
            // @note: for old telemetries we need to aggregate production and charge / discharge
            cT.P_DC_TOT = `${
              Number(cT.P_SELF || '') +
              Number(cT.P_EXT || '')
            }`;

            cT.P_B_AGGR = `${
              Number(cT.P_B1 || 0) + Number(cT.P_B2 || 0)
            }`;
          }

          const batCount = [cT.BP1, cT.BP2, cT.BP].filter(
            (b) => b !== null && b !== undefined && b !== '0' && Number(b) !== 0
          );
          cT.BP = `${
            Number(cT.BP1 || '') + Number(cT.BP2 || '') + Number(cT.BP || '')
          }`;
          cT.BP = `${Number(cT.BP) === 0 || batCount.length === 0 ? 0 : ( Number(cT.BP) / batCount.length)}`;
        } else {
          // @note ignore all the power incoming from not hybrid device
          cT.P_DC_TOT = '';
          cT.P_LOAD = '';
          cT.P_EXP = '';
          cT.P_B_AGGR = '';
        }

        // @note: only for 'Zp1es006n2p067' Fabrizio Bernini request //
        // Questo inverter non ha potenza consumata mai (nonostante ogni giorno segnali Enegia consumata)
        // tamponiamo calcolandola (questo valore solitamente lo prendiamo direttamente dall'inverter)
        if (deviceId.toLocaleLowerCase() === 'zp1es006n2p067'.toLocaleLowerCase() && Number(cT.P_LOAD) === 0) {
          cT.P_LOAD = `${Number(cT.P_DC_TOT) + Number(cT.P_EXP) + Number(cT.P_B_AGGR)}`;
        }

        delete cT.BP1;
        delete cT.BP2;
        delete cT.MP1;
        delete cT.MP2;
        delete cT.P7;
        delete cT.P8;

        return {
          P_DC_TOT: prev.P_DC_TOT + Number(cT.P_DC_TOT || ''),
          P_EXP: prev.P_EXP + Number(cT.P_EXP || ''),
          P_B_AGGR: prev.P_B_AGGR + Number(cT.P_B_AGGR || ''),
          P_LOAD: prev.P_LOAD + Number(cT.P_LOAD || ''),
          BP: prev.BP + (cT.BP ? Number(cT.BP || '') / devicesWhitBattery.length : 0),
          TS: prev.TS > Number(cT.TS) ? prev.TS : Number(cT.TS),
        };
      },
      {
        P_DC_TOT: 0,
        P_EXP: 0,
        P_B_AGGR: 0,
        P_LOAD: 0,
        BP: 0,
        TS: 0,
      }
    )
  );
};

export const getImportExportSystemPowerAggregate = (telemetry: {
  [key: string]: Partial<Inverter>;
}, invertMeterSensor?: boolean): Partial<Inverter> => {
  // @note: we want insert on the first positions all inverters
  // necessary to have a correct power when there are meters on the system
  const devices = Object.keys(telemetry).map((id) => (new ProductModelService(id)));
  const sortedKeyDevices = devices.filter((pm) => pm.type?.name === 'inverter').concat(
    devices.filter((pm) => pm.type?.name !== 'inverter')
  ).map((pm) => pm.id);

  return objectPropertiesToString(
    sortedKeyDevices.reduce(
      (prev, deviceId) => {
        const cT: Partial<Inverter & InverterV2> = clone(telemetry[deviceId]);
        const product = new ProductModelService(deviceId);

        cT.P_DC_TOT = cT.P_SELF;
        if (invertMeterSensor || invertMeterSensor === undefined) {
          cT.P_EXP = cT.P_EXP ? `${Number(cT.P_EXP) * -1}` : '';
        }
        if (isProductMeter(product) || isProductEnerclic(product)) {
          cT.P_EXP = cT.P_EXP ?? '';
          if (Number(cT.P_EXP) > 0 ) {
            cT.P_LOAD = `${ Number(cT.P_LOAD || '') - Number(cT.P_EXP) }`;
          } else {
            cT.P_LOAD = `${ Number(cT.P_LOAD || '') + (Number(cT.P_EXP) * -1 ) }`;
          }
        } else {
          cT.P_LOAD = `${ Number(cT.P_LOAD || '') + Number(cT.P_DC_TOT || '') }`;
        }

        return {
          P_DC_TOT: prev.P_DC_TOT + Number(cT.P_DC_TOT || ''),
          P_EXP: prev.P_EXP + Number(cT.P_EXP || ''),
          P_B_AGGR: 0,
          P_LOAD: prev.P_LOAD + Number(cT.P_LOAD || ''),
          BP: 0,
          TS: prev.TS > Number(cT.TS) ? prev.TS : Number(cT.TS),
        };
      },
      {
        P_DC_TOT: 0,
        P_EXP: 0,
        P_B_AGGR: 0,
        P_LOAD: 0,
        BP: 0,
        TS: 0,
      }
    )
  );
}

export const getPhotovoltaicSystemPowerAggregate = (telemetry: {
  [key: string]: Partial<Inverter>;
}): Partial<Inverter> => {
  // @note: we want insert on the first positions all inverters
  // necessary to have a correct power when there are meters on the system
  const devices = Object.keys(telemetry).map((id) => (new ProductModelService(id))).map((pm) => pm.id);
  return objectPropertiesToString(
    devices.reduce(
      (prev, deviceId) => {
        const cT: Partial<Inverter & InverterV2> = clone(telemetry[deviceId]);

        cT.P_DC_TOT = cT.P_SELF;

        return {
          P_DC_TOT: prev.P_DC_TOT + Number(cT.P_DC_TOT || ''),
          P_EXP: 0,
          P_B_AGGR: 0,
          P_LOAD: 0,
          BP: 0,
          TS: prev.TS > Number(cT.TS) ? prev.TS : Number(cT.TS),
        };
      },
      {
        P_DC_TOT: 0,
        P_EXP: 0,
        P_B_AGGR: 0,
        P_LOAD: 0,
        BP: 0,
        TS: 0,
      }
    )
  );
}

export const getSystemPowerAggregate = (plantType: PowerFlowGraphMode, inverterMeterSensor: boolean, telemetry: {
  [key: string]: Partial<Inverter>;
}): Observable<Partial<Inverter>> => {
  return of(getSnapshotPowerAggregate(telemetry, plantType, inverterMeterSensor))
}

export const getDailyPowerTelemetry = (
  data: { [key: string]: Array<Partial<Inverter>> },
  date: Date,
  offset: number,
  plantType: PowerFlowGraphMode,
  inverterMeterSensor: boolean,
): Observable<Array<Partial<Inverter>>> => {
  const BIN_SIZE = 5;
  // get latest TS & first TS
  let firstTs = 0;
  let latestTs = 0;
  Object.values(data).forEach((device) => {
    const first = device[0];
    const last = device[device.length - 1];
    if ((last.TS || 0) > latestTs) {
      latestTs = last.TS || 0;
    }
    if ((first.TS || 0) < firstTs || firstTs === 0) {
      firstTs = first.TS || 0;
    }
  });

  date.setHours(0 - date.getTimezoneOffset() / 60 - Number(offset));
  const midnight = date.getTime();

  const nextMidnight = midnight + 86400000;

  const dailyMinutes = (nextMidnight - midnight) / 1000 / 60;

  const bins = Array.from(
    { length: dailyMinutes / BIN_SIZE },
    (_, i) => midnight + i * BIN_SIZE * 60 * 1000
  );

  const result = {} as { [key: string]: Array<Partial<Inverter>> };

  const deviceIds = Object.keys(data);
  deviceIds.forEach((deviceId) => {
    const updatedTelemetry: Array<Partial<Inverter>> = [];
    const telemetry = data[deviceId];
    // Helper to find prev/next sample around a timestamp in ms
    const findPrevNext = (targetMs: number) => {
      let prev: Partial<Inverter> | undefined;
      let next: Partial<Inverter> | undefined;
      for (let i = 0; i < telemetry.length; i++) {
        const tMs = Number(telemetry[i].TS || 0) * 1000;
        if (tMs <= targetMs) {
          prev = telemetry[i];
        }
        if (tMs > targetMs) {
          next = telemetry[i];
          break;
        }
      }
      return { prev, next };
    };

    const collectKeys = (samples: Array<Partial<Inverter>>) => {
      const set = new Set<keyof Inverter>();
      samples.forEach(s => Object.keys(s || {}).forEach((k) => set.add(k as keyof Inverter)));
      return Array.from(set);
    };

    bins.forEach((binStart) => {
      const binEnd = binStart + BIN_SIZE * 60 * 1000;
      const tsCenter = binStart + (BIN_SIZE * 60 * 1000) / 2;

      const inBin = telemetry.filter((t) => {
        const currentTs = Number(t.TS || 0) * 1000;
        return currentTs >= binStart && currentTs < binEnd;
      });

      const currentTelemetry: Partial<Inverter> = {};

      if (inBin.length > 0) {
        // Determine prev/next samples to bound the bin segments
        const firstIndex = telemetry.indexOf(inBin[0]);
        const lastIndex = telemetry.indexOf(inBin[inBin.length - 1]);
        const prevSample = firstIndex > 0 ? telemetry[firstIndex - 1] : undefined;
        const nextSample = lastIndex < telemetry.length - 1 ? telemetry[lastIndex + 1] : undefined;

        const segmentSamples: Array<Partial<Inverter>> = [];
        if (prevSample) segmentSamples.push(prevSample);
        segmentSamples.push(...inBin);
        if (nextSample) segmentSamples.push(nextSample);

        const keys = collectKeys(segmentSamples);

        keys.forEach((key: any) => {
          if (key === 'TS' || key === '__typename') return;
          let weighted = 0;
          let coverage = 0;
          for (let i = 0; i < segmentSamples.length; i++) {
            const curr = segmentSamples[i];
            const currMs = Number(curr.TS || 0) * 1000;
            const nextMs = (i < segmentSamples.length - 1) ? Number(segmentSamples[i + 1].TS || 0) * 1000 : binEnd;
            const segStart = Math.max(binStart, currMs);
            const segEnd = Math.min(binEnd, nextMs);
            const dur = Math.max(0, segEnd - segStart);
            if (dur <= 0) continue;
            const val = Number((curr as any)[key]);
            if (!Number.isFinite(val)) continue;
            weighted += val * dur;
            coverage += dur;
          }
          if (coverage > 0) {
            (currentTelemetry as any)[key] = weighted / coverage;
          } else {
            // Fallback to simple mean if durations not available
            const values = inBin.map((s) => Number((s as any)[key])).filter((v) => Number.isFinite(v));
            if (values.length > 0) {
              (currentTelemetry as any)[key] = MathAvg(values);
            }
          }
        });
      } else {
        // Interpolate at bin center for short gaps
        const { prev, next } = findPrevNext(tsCenter);
        const MAX_GAP_MS = 20 * 60 * 1000; // 20 minutes tolerance
        if (prev && next) {
          const prevMs = Number(prev.TS || 0) * 1000;
          const nextMs = Number(next.TS || 0) * 1000;
          const gap = nextMs - prevMs;
          if (gap > 0 && gap <= MAX_GAP_MS) {
            const ratio = (tsCenter - prevMs) / gap;
            const keys = collectKeys([prev, next]);
            keys.forEach((key: any) => {
              if (key === 'TS' || key === '__typename') return;
              const v1 = Number((prev as any)[key]);
              const v2 = Number((next as any)[key]);
              if (Number.isFinite(v1) && Number.isFinite(v2)) {
                (currentTelemetry as any)[key] = v1 + (v2 - v1) * ratio;
              }
            });
          }
        }
      }

      updatedTelemetry.push({
        ...currentTelemetry,
        TS: tsCenter / 1000,
      });
    });

    result[deviceId] = updatedTelemetry;
  });

  let aggregateValues = bins.map((_, i) =>
    getSnapshotPowerAggregate(
      deviceIds.reduce(
        (curr, id) => ({ ...curr, [id]: result[id][i] || {} }),
        {} as { [key: string]: Partial<Inverter> }
      ),
      plantType,
      inverterMeterSensor,
    )
  );

  // filter data after the last TS
  aggregateValues = aggregateValues.filter((v) => Number(v.TS) <= latestTs);
  // filter data before the first TS
  aggregateValues = aggregateValues.filter((v) => Number(v.TS) >= firstTs);

  return of(aggregateValues);
};


export const getPowerTelemetry = (data: Partial<InverterV2>[], invertMeterSensor: boolean, serial: string): Partial<InverterV2>[] => {
  return data.map((d) => {
    d.P_DC_TOT = d.P_SELF;
    const product = new ProductModelService(serial);
    if(isProductEnerclic(product) || isProductMeter(product)) {
      if (invertMeterSensor || invertMeterSensor === undefined) {
        d.P_EXP = `${Number(d.P_EXP || '') * -1 }`
      }
    }

    if (product.type?.name === 'inverter' && (product.type.value === 'hybrid-3ph' || product.type.value === 'hybrid' )) {
      const batCount = [d.BP1, d.BP2, d.BP].filter((b) => b !== null && b !== undefined && b !== '0');
      d.P_B_AGGR = `${Number(d.P_B1 || '') + Number(d.P_B2 || '') }`;
      if (batCount.length > 0) {
        d.BP = `${Number(d.BP1 || '') + Number(d.BP2 || '') + Number(d.BP || '')}`;
        d.BP = `${Number(d.BP) / batCount.length}`;
      } else {
        d.BP = '0';
      }
    }
    return d;
  });
}
